
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, DOCUMENT } from '@angular/core';
import { ReadSpeakerConfigService, UUID_TOKEN } from '@campus/dal';
import { first, map, Observable, of } from 'rxjs';
import { ReadspeakerConfig } from './readspeaker.interface';

export interface ReadSpeakerRequestBody {
  audioformat: string; // usually: flv, but Audio elements can't play that so we choose mp3
  customerid: string;
  dict: string; // usually: default,customer
  html_base64: string;
  lang: string; // usually: nl_be
  readid: string; // usually: uuid
  rsjs_ver: string; // currently: 3.8.8_rev2834-wr
  rule: string; // usually: default,customer
  sync: string; // usually: user
  synccontainer: string; // usually: rs:span
  url: string; // usually: current location
  voice: string; // usually: Veerle-P
  speed: number; // default 100
  wrc: string; // usually: 2014018964
  xslrule: string; // usually: default,customer
}

@Injectable({ providedIn: 'root' })
export class ReadSpeakerService {
  private readonly readSpeakerCustomerId = '13371';
  private readonly readSpeakerBaseUrl = 'https://app-eu.readspeaker.com/cgi-bin';

  private document: Document = inject(DOCUMENT);
  private uuid: () => string = inject(UUID_TOKEN);
  private readSpeakerConfigService = inject(ReadSpeakerConfigService);
  private httpClient = inject(HttpClient);
  private currentAudio = null;

  private defaultRequestBody = {
    audioformat: 'mp3',
    customerid: this.readSpeakerCustomerId,
    dict: 'default,customer',
    lang: 'nl_be',
    rsjs_ver: '3.8.8_rev2834-wr',
    rule: 'default,customer',
    sync: 'user',
    synccontainer: 'rs:span',
    voice: 'Veerle-P',
    speed: 100,
    wrc: '2014018964',
    xslrule: 'default,customer',
  };

  /**
   * Reads the text of the element with the given reference ID.
   *
   * This intentionally does -not- work via a ReadSpeaker script to avoid conflicts with ReadSpeaker.Learnosity.
   * Instead, we make an API call to ReadSpeaker directly. The trade-off is no text highlighting.
   *
   * @param ref The reference ID of the element to read.
   * @param options Optional parameters for language and voice.
   */
  public readTextById(
    ref: string,
    options?: { lang?: string; voice?: string; ruleDictionary?: string; speed?: number }
  ): void {
    const html = this.document.getElementById(ref)?.innerHTML || '';
    this.readTextLiteral(html, options);
  }

  public readTextLiteral(
    literal: string,
    options?: { lang?: string; voice?: string; ruleDictionary?: string; speed?: number }
  ): void {
    const lang = options?.lang || this.defaultRequestBody.lang;
    const voice = options?.voice || this.defaultRequestBody.voice;
    const speed = options?.speed || this.defaultRequestBody.speed;
    const ruleDictionary = options?.ruleDictionary || this.defaultRequestBody.rule;

    const readSpeakerOptions: ReadSpeakerRequestBody = {
      ...this.defaultRequestBody,
      lang,
      voice,
      speed,
      dict: ruleDictionary,
      rule: ruleDictionary,
      xslrule: ruleDictionary,
      html_base64: btoa(literal),
      readid: this.uuid(),
      url: this.document.location.href,
    };

    this.readText(readSpeakerOptions);
  }

  private readText(options: ReadSpeakerRequestBody) {
    const requestUrl = new URL(`${this.readSpeakerBaseUrl}/rsent`);
    requestUrl.searchParams.set('logid', options.customerid);
    requestUrl.searchParams.set('ver', options.rsjs_ver);
    requestUrl.searchParams.set('wrc', options.wrc);

    const body = this.toPlainTextBody(options);

    this.httpClient
      .post(requestUrl.toString(), body, {
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        responseType: 'arraybuffer',
      })
      .subscribe({
        next: (response: ArrayBuffer) => {
          if (!response || response.byteLength === 0) {
            console.error('ReadSpeaker readTextById failed', response);
            return;
          }

          this.playReadSpeakerAudio(response);
        },
        error: (error) => {
          console.error('ReadSpeaker readTextById error', error);
        },
      });
  }

  private playReadSpeakerAudio(audio: ArrayBuffer) {
    const audioBlob = new Blob([audio], { type: 'audio/mpeg' });
    const audioUrl = URL.createObjectURL(audioBlob);

    this.resetAudioPlayer();
    this.currentAudio = new Audio(audioUrl);

    this.currentAudio.play().catch((error) => {
      console.error('Error playing audio:', error);
    });
  }

  private resetAudioPlayer() {
    if (!this.currentAudio) return;

    this.currentAudio.pause();
    this.currentAudio.currentTime = 0;
  }

  private toPlainTextBody(body: ReadSpeakerRequestBody) {
    return Object.entries(body)
      .reduce((acc, [key, value]) => {
        return [...acc, `${key}=${encodeURIComponent(value)}`];
      }, [])
      .join('&');
  }

  /**
   * Retrieves the ReadSpeaker configuration for a given ID.
   * If no ID is provided, it returns a default configuration.
   * @param readSpeakerConfigId
   */
  public getReadspeakerConfig(readSpeakerConfigId?: number): Observable<ReadspeakerConfig> {
    if (readSpeakerConfigId) {
      return this.readSpeakerConfigService.getReadSpeakerConfigById$(readSpeakerConfigId).pipe(
        first(),
        map((config) => ({
          lang: config.displayedLanguage || 'nl_be',
          voice: config.languageVoice || 'Veerle',
          speed: config.voiceSpeed || 100,
        }))
      );
    }
    return of({ lang: 'nl_be', voice: 'Veerle', speed: 100 });
  }
}
